import type { CompliancePersonResource, CompliancePersonSummary, ComplianceSnapshot } from "@/types/resourceCompliance";

export interface SnapshotResult {
  snapshot: ComplianceSnapshot;
  person: CompliancePersonSummary | null;
  resources: CompliancePersonResource[];
}

export interface DemoPerson { displayName: string; email: string }

export function canUseResourceHealthDemo(roles: readonly { role: string }[]): boolean {
  return roles.some(({ role }) => ["SuperAdmin", "ResourceComplianceAdmin", "ResourceHealthDemo"].includes(role));
}

export function demoHealthLoadError(status?: number): string {
  if (status === 403) return "Access denied. The signed-in account needs ResourceHealthDemo, ResourceComplianceAdmin, or SuperAdmin access. If the demo role was just enabled, make sure the backend has been restarted with the updated code.";
  if (status === 401) return "The API could not authenticate this request. If this repeats after signing in, restart the backend with the updated demo-role code.";
  return "Unable to load this person's health. Check your connection and select the person again to retry.";
}

export function normalizeDemoPeople(rows: unknown[]): DemoPerson[] {
  const people = new Map<string, DemoPerson>();
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const fields = row as Record<string, unknown>;
    const email = [fields.mail, fields.userPrincipalName, fields.email, fields.upn]
      .find((value): value is string => typeof value === "string" && value.trim().length > 0)?.trim();
    if (!email) continue;
    const displayName = typeof fields.displayName === "string" && fields.displayName.trim()
      ? fields.displayName.trim() : email;
    people.set(email.toLowerCase(), { displayName, email });
  }
  return [...people.values()].slice(0, 8);
}

export function orderHealthSnapshots(snapshots: ComplianceSnapshot[]): ComplianceSnapshot[] {
  return [...snapshots].sort((a, b) => Date.parse(b.snapshotDt) - Date.parse(a.snapshotDt));
}

export function snapshotLabel(snapshot: ComplianceSnapshot): string {
  return `${snapshot.snapshotName || snapshot.snapshotQuarter} (${new Date(snapshot.snapshotDt).toLocaleDateString()})`;
}

export function selectDemoSnapshots(snapshots: ComplianceSnapshot[], view: string): ComplianceSnapshot[] {
  return view === "compare" ? snapshots.slice(0, 2) : snapshots.filter((snapshot) => snapshot.snapshotId === view);
}

type MetricKey = {
  [K in keyof CompliancePersonSummary]: NonNullable<CompliancePersonSummary[K]> extends number ? K : never
}[keyof CompliancePersonSummary] & keyof CompliancePersonSummary;
const count = (value: number | null | undefined) => value == null ? "Not available" : value.toLocaleString();
const score = (value: number | null | undefined) => value == null ? "Not available" : `${value.toFixed(1)}%`;
const storage = (value: number | null | undefined) => value == null ? "Not available" : `${(value / 1024).toLocaleString(undefined, { maximumFractionDigits: 1 })} GB`;

export interface HealthMetric {
  key: MetricKey;
  label: string;
  format: (value: number | null | undefined) => string;
  kind: "score" | "count" | "storage" | "attention";
}

export function healthChangeTone(metric: HealthMetric, current: number | null | undefined, previous: number | null | undefined): "positive" | "negative" | "neutral" {
  if (current == null || previous == null || current === previous) return "neutral";
  if (metric.kind === "score") return current > previous ? "positive" : "negative";
  if (metric.kind === "attention") return current < previous ? "positive" : "negative";
  return "neutral";
}

export function comparisonWidths(metric: HealthMetric, current: number | null | undefined, previous: number | null | undefined): [number | null, number | null] {
  const max = metric.kind === "score" ? 100 : Math.max(current ?? 0, previous ?? 0, 1);
  const width = (value: number | null | undefined) => value == null ? null : Math.min(100, Math.max(0, value / max * 100));
  return [width(current), width(previous)];
}

export function orderDemoResources(resources: CompliancePersonResource[]): CompliancePersonResource[] {
  return [...resources].sort((a, b) =>
    (a.complianceScore ?? Infinity) - (b.complianceScore ?? Infinity) ||
    (a.title ?? "").localeCompare(b.title ?? ""));
}

export function formatHealthChange(metric: HealthMetric, current: number | null | undefined, previous: number | null | undefined): string {
  if (current == null || previous == null) return "Not available";
  const delta = current - previous;
  return `${delta > 0 ? "+" : ""}${metric.format(delta).replace("%", " pp")}`;
}

export const healthMetrics: HealthMetric[] = [
  { key: "directComplianceScore", label: "Owned resource health", format: score, kind: "score" },
  { key: "recursiveComplianceScore", label: "Accountable resource health", format: score, kind: "score" },
  { key: "resourcesOwned", label: "Resources owned", format: count, kind: "count" },
  { key: "resourcesAccountableFor", label: "Resources accountable for", format: count, kind: "count" },
  { key: "storageOwned", label: "Owned resource storage", format: storage, kind: "storage" },
  { key: "storageAccountableFor", label: "Accountable resource storage", format: storage, kind: "storage" },
  { key: "nonCompliantResourcesOwned", label: "Owned resources needing attention", format: count, kind: "attention" },
];
